import React from 'react';
import { isAfter, isFuture } from 'date-fns';
import { AxiosError } from 'axios';
import { DigitalOfferStatus, OfferDeedStatus, OfferStatus } from '../../../domain/types';
import { Intent, Offer, SignatureStatus } from '../../../providers/api/dtos';
import useOfferWizardData from '../use-offer-wizard-data/useOfferWizardData';
import useOfferSignatureStatus from '../use-offer-signature-status/useOfferSignatureStatus';
import useOfferAcceptanceSignatureStatus from '../use-offer-acceptance-signature-status/useOfferAcceptanceSignatureStatus';
import useOfferAcceptance from '../use-offer-acceptance/useOfferAcceptance';

type ComputedOfferStatusCanBeUpdatedTo = ComputedOfferStatus | 'SIGNATURE_REQUEST_CANCELLED' | 'SIGNATURE_REQUEST_DELETED';
export type ComputedOfferStatus = OfferStatus | DigitalOfferStatus | 'DEED_DONE' | 'NO_STATUS';

export type UseComputedOfferStatusData = {
  status: ComputedOfferStatus,
  canBeUpdated: boolean,
  canBeUpdatedTo: ComputedOfferStatusCanBeUpdatedTo[],
};

type UseComputedOfferStatusResult = {
  data?: UseComputedOfferStatusData | undefined,
  isLoading: boolean,
  errors?: Error[] | undefined,
  mutate: () => Promise<void>,
};

export const computedOfferStatusCanBeUpdatedTo: (signatureStatus: SignatureStatus | undefined) => Record<ComputedOfferStatus, ComputedOfferStatusCanBeUpdatedTo[]> = (signatureStatus) => ({
  NO_STATUS: [],
  IN_PROGRESS: [
    OfferStatus.REFUSED,
    OfferStatus.WITHDRAWN,
    DigitalOfferStatus.WAITING_FOR_ACCEPTANCE,
    DigitalOfferStatus.WAITING_FOR_COUNTER_OFFER_ACCEPTANCE,
  ],
  WAITING_FOR_ACCEPTANCE: [
    OfferStatus.IN_PROGRESS,
    OfferStatus.REFUSED,
  ],
  WAITING_FOR_COUNTER_OFFER_ACCEPTANCE: [
    OfferStatus.IN_PROGRESS,
    OfferStatus.REFUSED,
  ],
  ACCEPTED: [
    OfferStatus.CONFIRMED,
    OfferStatus.MORTGAGE_REFUSED,
    OfferStatus.OTHER_CONSTRAINTS,
  ],
  CONFIRMED: ['DEED_DONE'],
  DEED_DONE: [],
  MORTGAGE_REFUSED: [],
  OTHER_CONSTRAINTS: [],
  REFUSED: [],
  WITHDRAWN: [],
  WAITING_FOR_SIGNATURE: [signatureStatus === SignatureStatus.ONGOING ? 'SIGNATURE_REQUEST_CANCELLED' : 'SIGNATURE_REQUEST_DELETED'],
});

export function useComputedOfferStatus(
  intentId?: NonNullable<Intent['id']>,
  offer?: Offer,
): UseComputedOfferStatusResult {
  const [errors, setErrors] = React.useState(
    offer && offer.intentId !== intentId
      ? [new Error(`Offer must be related to the intent with id ${intentId}`)]
      : undefined,
  );

  const handleNewError = React.useCallback((err: AxiosError) => {
    if (err.response?.status && [400, 404].includes(err.response.status)) return;

    setErrors((prev) => {
      if (!prev) return [err];
      return [...prev, err];
    });
  }, []);

  const {
    data: wizardData,
    isLoading: isWizardDataLoading,
    mutate: mutateWizardData,
  } = useOfferWizardData(intentId, { onError: handleNewError });

  const {
    data: signatureData,
    isLoading: isSignatureStatusLoading,
    mutate: mutateSignatureStatus,
  } = useOfferSignatureStatus(intentId, { onError: handleNewError });

  const {
    data: ackSignatureData,
    mutate: mutateAckSignatureStatus,
  } = useOfferAcceptanceSignatureStatus(offer?.id, {
    onError: (error) => {
      handleNewError(error);
      /* SWR non invalida la cache presente quando la chiamata fallisce.
      * Quando annulliamo una richiesta di firma ritorniamo errore con status code 400 dal backend.
      * Per computare correttamente lo stato della richiesta di firma dell'accettazione occorre impostare la cache a undefined.
      */
      if (error.response?.status === 400) {
        mutateAckSignatureStatus(undefined, { revalidate: false });
      }
    },
  });

  const {
    data: offerAcceptanceData,
    mutate: mutateOfferAcceptance,
  } = useOfferAcceptance(offer?.id, { onError: handleNewError });

  const computedStatus: ComputedOfferStatus = React.useMemo(() => {
    if (offer && (!wizardData?.isDigitallySigned || isAfter(new Date(offer.updatedAt!), new Date(wizardData.updatedAt!)))
    ) {
      if (offer.deed?.status === OfferDeedStatus.CONFIRMED && !isFuture(offer.deed.date)) {
        return 'DEED_DONE';
      }

      if (wizardData?.isDigitallySigned && offer.status === OfferStatus.IN_PROGRESS && ackSignatureData?.status === SignatureStatus.ONGOING) {
        if (offerAcceptanceData?.counterOffer) {
          return DigitalOfferStatus.WAITING_FOR_COUNTER_OFFER_ACCEPTANCE;
        }

        return DigitalOfferStatus.WAITING_FOR_ACCEPTANCE;
      }

      return offer.status;
    }

    if (wizardData?.isDigitallySigned) {
      return DigitalOfferStatus.WAITING_FOR_SIGNATURE;
    }

    return 'NO_STATUS';
  }, [ackSignatureData?.status, offer, offerAcceptanceData, wizardData?.isDigitallySigned, wizardData?.updatedAt]);

  const mutate = React.useCallback(async () => {
    await mutateWizardData();
    await mutateSignatureStatus();
    await mutateAckSignatureStatus();
    await mutateOfferAcceptance();
  }, [mutateAckSignatureStatus, mutateOfferAcceptance, mutateSignatureStatus, mutateWizardData]);

  const isLoading = React.useMemo(
    () => isWizardDataLoading || isSignatureStatusLoading,
    [isSignatureStatusLoading, isWizardDataLoading],
  );

  const data: UseComputedOfferStatusData = React.useMemo(() => {
    const canBeUpdatedTo = computedOfferStatusCanBeUpdatedTo(signatureData?.status)[computedStatus];

    return {
      status: computedStatus,
      canBeUpdated: canBeUpdatedTo.length > 0,
      canBeUpdatedTo,
    };
  }, [computedStatus, signatureData?.status]);

  return React.useMemo(() => ({
    data,
    isLoading,
    errors,
    mutate,
  }), [data, errors, isLoading, mutate]);
}
